import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../App";
import { Card } from "./ui/card";
import { Loader2, Crown, RefreshCw, AlertTriangle, TrendingUp } from "lucide-react";
import { Button } from "./ui/button";

export default function LeadershipSnapshot() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/leadership/report?period=month");
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 403) setData({ forbidden: true });
      else setData({ error: e?.response?.data?.detail || "Failed to load" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Card className="p-4 border-slate-200" data-testid="leadership-snapshot-loading">
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading leadership report…
        </div>
      </Card>
    );
  }
  if (!data || data.forbidden || data.error) return null;

  const fmtGBP = (n) => `£${Math.round(n || 0).toLocaleString("en-GB")}`;
  const top = data.topManagers?.[0];
  const bottom = data.bottomManagers?.[data.bottomManagers.length - 1];

  return (
    <Card className="p-4 border-slate-200 bg-white" data-testid="leadership-snapshot">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Crown className="h-4 w-4 text-indigo-600" />
          <h3 className="font-semibold text-slate-900 text-sm">Leadership Snapshot</h3>
          <span className="text-xs text-slate-500">monthly</span>
        </div>
        <Button variant="ghost" size="sm" onClick={load} className="h-7 px-2" data-testid="leadership-refresh">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
        <div className="rounded-md p-2.5 bg-emerald-50 border border-emerald-200" data-testid="leadership-top">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Top manager</div>
          <div className="text-sm text-slate-900 font-medium truncate mt-0.5">{top?.name || "—"}</div>
          <div className="text-[11px] text-emerald-700">{top?.grade || ""} · {fmtGBP(top?.valueCapturedGBP)}</div>
        </div>
        <div className="rounded-md p-2.5 bg-rose-50 border border-rose-200" data-testid="leadership-bottom">
          <div className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold">Bottom manager</div>
          <div className="text-sm text-slate-900 font-medium truncate mt-0.5">{bottom?.name || "—"}</div>
          <div className="text-[11px] text-rose-700">{bottom?.grade || ""} · score {bottom?.score || 0}</div>
        </div>
        <div className="rounded-md p-2.5 bg-amber-50 border border-amber-200" data-testid="leadership-leaks">
          <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Open revenue leaks</div>
          <div className="text-sm text-slate-900 font-semibold mt-0.5">{fmtGBP(data.openRevenueLeaksGBP)}</div>
          <div className="text-[11px] text-amber-700">at risk this month</div>
        </div>
        <div className="rounded-md p-2.5 bg-indigo-50 border border-indigo-200" data-testid="leadership-promotions">
          <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Promotions suggested</div>
          <div className="text-sm text-slate-900 font-semibold mt-0.5">{data.promotionsSuggested?.length || 0}</div>
          <div className="text-[11px] text-indigo-700 truncate">
            {(data.promotionsSuggested || []).slice(0, 2).map(m => m.name).join(", ") || "Keep raising the standard"}
          </div>
        </div>
        <div className="rounded-md p-2.5 bg-rose-50 border border-rose-200" data-testid="leadership-urgent">
          <div className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Urgent interventions</div>
          <div className="text-sm text-slate-900 font-semibold mt-0.5">{data.urgentInterventions?.length || 0}</div>
          <div className="text-[11px] text-rose-700 truncate">
            {(data.urgentInterventions || []).slice(0, 2).map(m => m.name).join(", ") || "No one in Risk band"}
          </div>
        </div>
        <div className="rounded-md p-2.5 bg-slate-50 border border-slate-200" data-testid="leadership-response">
          <div className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Avg response time</div>
          <div className="text-sm text-slate-900 font-semibold mt-0.5">{data.avgManagerResponseTime || "—"}</div>
          <div className="text-[11px] text-slate-500">{data.managerCount} managers · {fmtGBP(data.totalValueCapturedGBP)} captured</div>
        </div>
      </div>
    </Card>
  );
}
